import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  playRocketThrusterSound, 
  playCorrectChime, 
  playWrongBuzzer, 
  playWinFanfare, 
  playLaunchChime 
} from '../utils/soundEffects';
import { getQuizQuestions } from '../utils/quizData';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiClock, FiMaximize2, FiMinimize2, FiRefreshCw, FiSun, FiMoon, FiHome, FiCheckCircle, FiCompass, FiZap } = FiIcons;

const CHANDRAYAAN_QUESTIONS = [
  {
    question: "What is the gravitational pull on the Moon compared to Earth?",
    options: ["Same as Earth", "1/6th of Earth's Gravity", "Double of Earth's Gravity", "Zero Gravity"],
    correctIndex: 1,
    fact: "Because the Moon's gravity is 1/6th of Earth, you can jump 6 times higher on the Moon!"
  },
  {
    question: "Why does the Moon have no sound or weather?",
    options: ["It is too hot", "It has no atmosphere / air", "It rotates too fast", "It is made of rock"],
    correctIndex: 1,
    fact: "Sound waves require air or a medium to travel; space and the Moon are a vacuum!"
  },
  {
    question: "Which region of the Moon did India's Chandrayaan-3 land on?",
    options: ["North Pole", "Equator", "South Pole", "Dark Side of the Moon"],
    correctIndex: 2,
    fact: "India made history as the very first country in the world to land near the Lunar South Pole!"
  },
  {
    question: "What was the name of the 6-wheeled robotic Rover deployed by the Vikram lander?",
    options: ["Pragyan", "Pushpak", "Gaganyaan", "Aryabhata"],
    correctIndex: 0,
    fact: "Pragyan (meaning 'Wisdom' in Sanskrit) conducted scientific experiments on lunar soil!"
  },
  {
    question: "Which fuel type or law of motion pushes a rocket forward in space?",
    options: ["Newton's 1st Law", "Newton's 3rd Law (Action-Reaction)", "Ohm's Law", "Archimedes Principle"],
    correctIndex: 1,
    fact: "As hot exhaust gases shoot downward at high speed, the rocket is pushed upward with equal force!"
  },
  {
    question: "What vital resource was first detected on the Moon by India's Chandrayaan-1 in 2008?",
    options: ["Gold Nuggets", "Liquid Petrol", "Water Ice Molecules (H2O)", "Diamond Crystals"],
    correctIndex: 2,
    fact: "Chandrayaan-1's Moon Mineralogy Mapper confirmed the presence of water molecules on the Moon!"
  }
];

const INITIAL_HOUSES = [
  { name: "Red House", label: "Ganga", color: "from-red-500 to-rose-600", score: 0 },
  { name: "Blue House", label: "Yamuna", color: "from-blue-500 to-cyan-600", score: 0 },
  { name: "Green House", label: "Kaveri", color: "from-emerald-500 to-teal-600", score: 0 },
  { name: "Yellow House", label: "Narmada", color: "from-amber-400 to-orange-500", score: 0 }
];

const MissionChandrayaan = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [activeQuestions, setActiveQuestions] = useState(() => {
    const custom = getQuizQuestions().filter(q => 
      q.category?.toLowerCase().includes('science') || 
      q.category?.toLowerCase().includes('space') ||
      q.category?.toLowerCase().includes('astronomy')
    );
    return custom.length >= 3 ? custom : CHANDRAYAAN_QUESTIONS;
  });

  const [altitude, setAltitude] = useState(30); // in kilometers
  const [velocity, setVelocity] = useState(1200); // in m/s
  const [fuel, setFuel] = useState(100); // percentage
  const [qIndex, setQIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isLanded, setIsLanded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false); // Day mode default with light shades
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [houses, setHouses] = useState(INITIAL_HOUSES);

  useEffect(() => {
    const handleUpdate = () => {
      const custom = getQuizQuestions().filter(q => 
        q.category?.toLowerCase().includes('science') || 
        q.category?.toLowerCase().includes('space') ||
        q.category?.toLowerCase().includes('astronomy')
      );
      if (custom.length >= 3) setActiveQuestions(custom);
    };
    window.addEventListener('quizQuestionsUpdated', handleUpdate);
    return () => window.removeEventListener('quizQuestionsUpdated', handleUpdate);
  }, []);

  const currentQ = activeQuestions[qIndex % activeQuestions.length] || CHANDRAYAAN_QUESTIONS[0];

  const handleSelectOption = (idx) => {
    if (isAnswered) return;
    setSelectedOption(idx);
    setIsAnswered(true);

    if (idx === currentQ.correctIndex) {
      playCorrectChime();
      playRocketThrusterSound(1.2);

      // Successfully brake lander
      const nextAltitude = Math.max(0, altitude - 6);
      const nextVelocity = Math.max(0, Math.round(velocity * 0.45));
      const nextFuel = Math.max(10, fuel - 15);

      setAltitude(nextAltitude);
      setVelocity(nextVelocity);
      setFuel(nextFuel);

      if (nextAltitude === 0) {
        setTimeout(() => {
          setIsLanded(true);
          playLaunchChime();
          setTimeout(() => playWinFanfare(), 600);
        }, 800);
      }
    } else {
      playWrongBuzzer();
    }
  };

  const handleNextQuestion = () => {
    setSelectedOption(null);
    setIsAnswered(false);
    setQIndex((prev) => (prev + 1) % CHANDRAYAAN_QUESTIONS.length);
  };

  const handleResetMission = () => {
    setAltitude(30);
    setVelocity(1200);
    setFuel(100);
    setQIndex(0);
    setSelectedOption(null);
    setIsAnswered(false);
    setIsLanded(false);
    playLaunchChime();
  };

  const addHouseScore = (idx, pts) => {
    setHouses(prev => prev.map((h, i) => i === idx ? { ...h, score: Math.max(0, h.score + pts) } : h));
    playCorrectChime();
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 select-none ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-slate-100 via-sky-50 to-blue-50 text-slate-800'
    }`}>
      {isLanded && <ConfettiEffect />}

      {/* Top Projector Header */}
      <header className={`flex flex-wrap items-center justify-between gap-4 pb-4 border-b transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-slate-300 bg-white/70 backdrop-blur-md rounded-2xl px-4 shadow-sm'
      }`}>
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all shadow-sm ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-300 hover:text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-3xl">🚀</span>
              <h1 className="text-2xl md:text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-amber-500 to-emerald-600">
                Mission Chandrayaan: Lunar Landing
              </h1>
            </div>
            <p className={`text-xs font-bold uppercase tracking-widest ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Live Stage & Classroom Simulator • ISRO South Pole Descent
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 cursor-pointer shadow-md transition-all ${
              isDarkMode ? 'border-slate-700 bg-slate-900 text-amber-300' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
            <span>{isDarkMode ? 'Night Mode 🌌' : 'Day Mode ☀️'}</span>
          </button>

          <button 
            onClick={toggleFullscreen}
            className={`p-2.5 rounded-full border transition-all cursor-pointer ${
              isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-300 hover:text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 shadow-sm'
            }`}
            title="Fullscreen"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="text-lg" />
          </button>
        </div>
      </header>

      {/* Main Descent Arena */}
      <main className="flex-1 flex flex-col lg:flex-row items-center justify-between gap-8 my-6 max-w-6xl mx-auto w-full">
        
        {/* Left Side: Live Telemetry Gauges & Lunar Surface */}
        <div className="w-full lg:w-5/12 flex flex-col items-center">
          
          {/* Telemetry Box */}
          <div className={`w-full grid grid-cols-3 gap-3 mb-6 p-4 rounded-2xl border shadow-xl transition-all ${
            isDarkMode ? 'bg-slate-900/80 border-slate-700 text-white' : 'bg-white border-slate-300 shadow-lg text-slate-800'
          }`}>
            <div className="text-center">
              <span className={`text-[10px] font-bold uppercase tracking-wider block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Altitude</span>
              <span className="text-2xl sm:text-3xl font-black font-mono text-cyan-500">{altitude} km</span>
            </div>
            <div className={`text-center border-x ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wider block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Descent Speed</span>
              <span className="text-2xl sm:text-3xl font-black font-mono text-amber-500">{velocity} m/s</span>
            </div>
            <div className="text-center">
              <span className={`text-[10px] font-bold uppercase tracking-wider block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Thruster Fuel</span>
              <span className="text-2xl sm:text-3xl font-black font-mono text-emerald-500">{fuel}%</span>
            </div>
          </div>

          {/* Visual Lander Descent Chamber */}
          <div className="w-full h-80 rounded-3xl border-2 border-slate-700 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800 relative overflow-hidden flex flex-col justify-between items-center p-4 shadow-2xl">
            
            {/* Stars background */}
            <div className="absolute top-4 left-6 text-xs text-slate-500">✨</div>
            <div className="absolute top-12 right-10 text-xs text-slate-600">⭐</div>
            <div className="absolute top-24 left-1/3 text-xs text-slate-400">✨</div>

            {/* Earth in distance */}
            <div className="absolute top-3 left-4 flex items-center gap-1.5 opacity-80">
              <span className="text-2xl">🌍</span>
              <span className="text-[10px] font-mono text-cyan-300">Earth: 384,400 km</span>
            </div>

            {/* Vikram Lander Craft */}
            <motion.div
              animate={{
                y: isLanded ? 160 : (30 - altitude) * 4.8,
                rotate: isLanded ? 0 : [0, -2, 2, 0]
              }}
              transition={{ type: "spring", stiffness: 60, damping: 15 }}
              className="relative z-20 flex flex-col items-center"
            >
              <div className="text-6xl sm:text-7xl filter drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                🛸
              </div>
              {/* Thruster Plume */}
              {!isLanded && selectedOption === currentQ.correctIndex && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: [1, 0.7, 1], scale: [1, 1.3, 1] }}
                  transition={{ repeat: 3, duration: 0.2 }}
                  className="text-2xl -mt-2 text-orange-400 filter drop-shadow-[0_0_12px_rgba(249,115,22,0.8)]"
                >
                  🔥
                </motion.div>
              )}
            </motion.div>

            {/* Lunar Surface Ground */}
            <div className="w-full relative z-10 pt-4 flex flex-col items-center border-t-4 border-slate-600">
              <div className="flex gap-8 text-2xl opacity-60">
                <span>🌑</span>
                <span>🪨</span>
                <span>🌑</span>
              </div>
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 mt-1">
                {isLanded ? "🎉 Soft Landing Achieved: Shiv Shakti Point!" : "Lunar South Pole Surface"}
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: STEM Challenge & Answer Cards */}
        <div className="w-full lg:w-7/12">
          <AnimatePresence mode="wait">
            {isLanded ? (
              <motion.div
                key="landed"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="p-8 rounded-3xl bg-gradient-to-br from-emerald-950/80 via-slate-900 to-indigo-950 border-2 border-emerald-500/60 shadow-2xl text-center"
              >
                <div className="text-6xl mb-3 animate-bounce">🇮🇳</div>
                <div className="text-xs font-black uppercase tracking-widest text-emerald-400 mb-1">
                  National Space Achievement
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-2">
                  Touchdown Confirmed!
                </h2>
                <p className="text-sm text-slate-300 max-w-md mx-auto mb-6">
                  Vikram lander has touched down safely! Pragyan rover has rolled out onto the lunar surface to search for water ice and minerals!
                </p>

                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={handleResetMission}
                    className="px-6 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/30 hover:scale-105 active:scale-95 transition-transform cursor-pointer"
                  >
                    <SafeIcon icon={FiRefreshCw} />
                    <span>Launch Next Mission</span>
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={qIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-6 sm:p-8 rounded-3xl border shadow-2xl transition-all ${
                  isDarkMode 
                    ? 'bg-slate-900/90 border-slate-700 text-white' 
                    : 'bg-white border-slate-300 text-slate-900 shadow-xl'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-black uppercase bg-indigo-500/20 text-indigo-500 dark:text-indigo-400 border border-indigo-500/30">
                    STEM Descent Challenge #{qIndex + 1}
                  </span>
                  <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Correct answers fire retro-thrusters!
                  </span>
                </div>

                <h3 className={`text-lg sm:text-xl font-black mb-6 leading-relaxed ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  {currentQ.question}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {currentQ.options.map((opt, idx) => {
                    const isCorrect = idx === currentQ.correctIndex;
                    const isChosen = selectedOption === idx;

                    let btnStyle = isDarkMode
                      ? 'bg-slate-800/80 hover:bg-slate-700/80 border-slate-700 text-slate-200 hover:border-indigo-400'
                      : 'bg-slate-50 hover:bg-slate-100 border-slate-300 text-slate-800 hover:border-indigo-500 shadow-sm';

                    if (isAnswered) {
                      if (isCorrect) {
                        btnStyle = 'bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-300 font-black';
                      } else if (isChosen) {
                        btnStyle = 'bg-red-500/20 border-red-500 text-red-600 dark:text-red-300 font-black';
                      } else {
                        btnStyle = isDarkMode 
                          ? 'bg-slate-800/40 border-slate-700 text-slate-500 opacity-60' 
                          : 'bg-slate-100 border-slate-200 text-slate-400 opacity-60';
                      }
                    }

                    return (
                      <button
                        key={idx}
                        disabled={isAnswered}
                        onClick={() => handleSelectOption(idx)}
                        className={`p-4 rounded-2xl text-left font-bold text-sm border-2 transition-all flex items-center justify-between cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${btnStyle}`}
                      >
                        <span>{opt}</span>
                        {isAnswered && isCorrect && <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-lg" />}
                      </button>
                    );
                  })}
                </div>

                {isAnswered && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-2xl border mb-4 flex flex-col sm:flex-row items-center justify-between gap-3 ${
                      isDarkMode 
                        ? 'bg-indigo-950/60 border-indigo-500/40 text-indigo-200' 
                        : 'bg-indigo-50 border-indigo-200 text-indigo-950 shadow-sm'
                    }`}
                  >
                    <div className="text-xs font-medium">
                      💡 <span className="font-bold">Science Fact:</span> {currentQ.fact}
                    </div>
                    <button
                      onClick={handleNextQuestion}
                      className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs whitespace-nowrap shadow-md cursor-pointer"
                    >
                      Next Question →
                    </button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Bottom House Scoreboard */}
      <footer className={`pt-4 border-t transition-colors ${isDarkMode ? 'border-slate-800' : 'border-slate-300'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className={`text-xs font-black uppercase tracking-wider flex items-center gap-2 ${
            isDarkMode ? 'text-slate-400' : 'text-slate-600'
          }`}>
            <SafeIcon icon={FiAward} className="text-amber-500" />
            <span>School House Flight Controllers</span>
          </div>
          <button 
            onClick={() => setHouses(INITIAL_HOUSES)}
            className={`text-[11px] font-bold flex items-center gap-1 cursor-pointer transition-colors ${
              isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <SafeIcon icon={FiRefreshCw} /> Reset House Points
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {houses.map((house, idx) => (
            <div 
              key={idx}
              className={`p-3 rounded-2xl border flex items-center justify-between gap-2 shadow-sm transition-all ${
                isDarkMode 
                  ? 'border-slate-800 bg-slate-900/80 text-white' 
                  : 'border-slate-300 bg-white text-slate-800 shadow-md'
              }`}
            >
              <div>
                <div className={`text-xs font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{house.label}</div>
                <div className={`text-[10px] font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{house.name}</div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xl font-black font-mono text-cyan-500">{house.score}</span>
                <button
                  onClick={() => addHouseScore(idx, 10)}
                  className={`w-7 h-7 rounded-xl bg-gradient-to-br ${house.color} text-white font-bold text-xs flex items-center justify-center shadow-md hover:scale-110 active:scale-95 transition-transform cursor-pointer`}
                >
                  +10
                </button>
              </div>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default MissionChandrayaan;
